import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_DOMAIN } from "config";
import J5StatusBadge from "components/J5StatusBadge";
import useInfiniteScroll from "components/UseInfiniteScroll";
import "./BillingAccounts.css";
import {
  getDealType,
  getJ5Status,
  isOpenJ5Status,
  isReleasedJ5Status,
} from "utils/j5Status";

const roleConfig = {
  dealer: {
    title: "חשבונות",
    subtitle: "חיובים , J5 פעילים ומשחוררים .",
    endpoint: "/billing/dealerAccounts",
    allowedRole: 3,
    summaryLabels: ["סה״כ עסקאות שחויבו", "שריונים פעילים", "שריונים ששוחררו", "עסקאות ללא שריון"],
    showSupplier: true,
    showDealer: false,
    showAdminFilters: false,
    showFilters: true,
    actionColumn: false,
  },
  supplier: {
    title: "חשבונות",
    subtitle: "עסקאות שאישרת וסטטוס השריון מול המנהל.",
    endpoint: "/billing/supplierAccounts",
    allowedRole: 2,
    summaryLabels: ["עסקאות שאושרו", "J5 פתוחות", "J5 ששוחררו", "עסקאות ללא שריון"],
    showSupplier: false,
    showDealer: true,
    showAdminFilters: false,
    showFilters: true,
    actionColumn: false,
  },
  admin: {
    title: "עסקאות שאושרו",
    subtitle: "תצוגת מנהל לכל העסקאות המאושרות, כולל J5 פתוחות, שחרורים ועסקאות ללא שריון.",
    endpoint: "/billing/adminApprovedDeals",
    allowedRole: 1,
    summaryLabels: ["סה״כ עסקאות שאושרו", "J5 פתוחות", "J5 ששוחררו", "J5 ממתינות לחיוב מנהל", "עסקאות ללא שריון"],
    showSupplier: true,
    showDealer: true,
    showAdminFilters: true,
    showFilters: true,
    actionColumn: true,
  },
};

const initialFilters = {
  j5Status: "all",
  offerStatus: "all",
  dateFrom: "",
  dateTo: "",
  search: "",
};

const ITEMS_PER_LOAD = 7;

const DEAL_CHARGE_DISABLED_TOOLTIP = "חיוב עסקה אינו זמין לעסקה זו";

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "-";
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("he-IL", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function vehicleLabel(item) {
  return [item.manufacturer, item.model, item.sub_model, item.year, item.license_plate]
    .filter(Boolean)
    .join(" ");
}

function releaseReasonLabel(reason) {
  if (reason === "released_after_full_deal_charge") {
    return "השריון שוחרר לאחר חיוב העסקה";
  }

  const labels = {
    released_after_48h_from_supplier_approval: "השריון שוחרר לאחר 48 שעות מאישור העסקה",
    loser_after_winner_selected: "השריון שוחרר כיוון שההצעה נדחתה",
expired_48h: "השריון שוחרר לאחר 48 שעות, כיוון שהספק לא אישר את ההצעה",  };

  return labels[reason] || reason || "-";
}

function offerStatusKey(item) {
  const status = Number(item.offer_status);
  if (status === 2) return "approved";
  if (status === 3) return "rejected";
  return "pending";
}

function offerStatusLabel(item) {
  if (offerStatusKey(item) === "approved" && isImmediatePurchase(item)) {
    return "אושרה ברכישה מידית";
  }

  const labels = {
    approved: "אושרה",
    rejected: "נדחתה",
    pending: "ממתין לאישור",
  };

  return labels[offerStatusKey(item)];
}

function transactionLabel(item) {
  if (isImmediatePurchaseCharged(item)) {
    return "חיוב רכישה מידית בוצע";
  }

  const status = truthy(item.deal_charge_succeeded)
    ? "success"
    : item.last_billing_transaction_status;
  return String(status || "").toLowerCase() === "success" ? "העסקה חויבה" : "-";
}

function offerSubmissionDate(item) {
  return item.offer_created_at || "";
}

function freeSearchText(item, role) {
  return [
    ...Object.values(item || {}),
    vehicleLabel(item),
    offerStatusLabel(item),
    transactionLabel(item),
    getJ5Status(item, role).label,
    getJ5Status(item, role).description,
    releaseReasonLabel(item.release_reason),
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .join(" ")
    .toLowerCase();
}

function calcSummary(items, role) {
  return items.reduce(
    (acc, item) => {
      const status = getJ5Status(item, role);

      acc.total += 1;
      if (["captured", "immediate_purchase_charged"].includes(status.statusKey)) acc.charged += 1;
      if (!status.isJ5) acc.noJ5 += 1;
      if (isOpenJ5Status(status.statusKey)) acc.open += 1;
      if (isReleasedJ5Status(status.statusKey)) acc.released += 1;
      if (status.statusKey === "pending_capture") acc.pendingAdmin += 1;

      return acc;
    },
    { total: 0, charged: 0, open: 0, released: 0, pendingAdmin: 0, noJ5: 0 }
  );
}

function truthy(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function isImmediatePurchase(item) {
  return (
    truthy(item?.immediate_purchase) ||
    truthy(item?.immediate_purchase_succeeded) ||
    String(item?.last_billing_transaction_type || "") === "immediate_purchase"
  );
}

function isImmediatePurchaseCharged(item) {
  return (
    truthy(item?.immediate_purchase_succeeded) ||
    (
      isImmediatePurchase(item) &&
      String(item?.last_billing_transaction_status || "").toLowerCase() === "success"
    )
  );
}

function chargeAmount(item) {
  if (isImmediatePurchaseCharged(item)) {
    return item.immediate_purchase_amount || item.deal_charge_amount;
  }
  return item.deal_charge_amount;
}

function chargeCreatedAt(item) {
  if (isImmediatePurchaseCharged(item)) {
    return item.immediate_purchase_created_at || item.deal_charge_created_at;
  }
  return item.deal_charge_created_at;
}

function chargePaymeSaleId(item) {
  if (isImmediatePurchaseCharged(item)) {
    return item.immediate_purchase_payme_sale_id || item.deal_charge_payme_sale_id;
  }
  return item.deal_charge_payme_sale_id;
}

function primaryPaymeSaleId(item) {
  return chargePaymeSaleId(item) || item.payme_sale_id;
}

function rowKeyForItem(item) {
  return `${item.offer_id || "offer"}-${item.reservation_hold_id_actual || item.reservation_hold_id || "no-hold"}`;
}

function dealTypeLabel(item, status) {
  if (isImmediatePurchase(item)) return "רכישה מידית";
  if (status.statusKey === "captured") return "חויב בפועל";
  if (isReleasedJ5Status(status.statusKey)) return "שוחרר";
  if (isOpenJ5Status(status.statusKey)) return "שריון פעיל";
  if (!status.isJ5) return "ללא שריון";
  return getDealType(item);
}

function filterAccountItems(items, filters, role) {
  const search = filters.search.trim().toLowerCase();

  return items.filter((item) => {
    const status = getJ5Status(item, role);

    if (filters.j5Status === "no_j5" && status.isJ5) return false;
    if (filters.j5Status === "pending_capture" && status.statusKey !== "pending_capture") return false;
    if (filters.j5Status === "j5_active" && status.statusKey !== "j5_active") return false;
    if (
      filters.j5Status === "captured" &&
      !["captured", "immediate_purchase_charged"].includes(status.statusKey)
    ) return false;
    if (filters.j5Status === "released" && !isReleasedJ5Status(status.statusKey)) return false;
    if (filters.j5Status === "failed" && status.statusKey !== "failed") return false;
    if (filters.j5Status === "capture_pending" && status.statusKey !== "capture_pending") return false;

    if (filters.offerStatus !== "all" && offerStatusKey(item) !== filters.offerStatus) return false;

    const submittedDate = String(offerSubmissionDate(item)).slice(0, 10);
    if (filters.dateFrom && (!submittedDate || submittedDate < filters.dateFrom)) return false;
    if (filters.dateTo && (!submittedDate || submittedDate > filters.dateTo)) return false;

    if (!search) return true;

    return freeSearchText(item, role).includes(search);
  });
}

function SummaryCard({ label, value }) {
  return (
    <div className="billing-summary-card">
      <div className="billing-summary-label">{label}</div>
      <div className="billing-summary-value">{value}</div>
    </div>
  );
}

function DealChargeConfirmModal({ item, loading, error, onCancel, onConfirm }) {
  if (!item) return null;

  return (
    <div className="billing-charge-modal-overlay" role="presentation" onMouseDown={onCancel}>
      <div
        className="billing-charge-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-charge-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="billing-charge-modal-close"
          onClick={onCancel}
          aria-label="סגור"
          disabled={loading}
        >
          ×
        </button>

        <div className="billing-charge-modal-icon" aria-hidden="true">
          ₪
        </div>

        <h2 id="billing-charge-modal-title">אישור חיוב עסקה</h2>
        <p className="billing-charge-modal-text">
          האם לבצע חיוב לעסקה בסך <strong>{formatMoney(item.offered_price)}</strong>?
        </p>

        <div className="billing-charge-modal-summary">
          <div>
            <span>רכב</span>
            <strong>{vehicleLabel(item) || "-"}</strong>
          </div>
          <div>
            <span>ספק</span>
            <strong>{item.supplier_name || "-"}</strong>
          </div>
          <div>
            <span>סוחר</span>
            <strong>{item.dealer_name || "-"}</strong>
          </div>
        </div>

        {error && (
          <div className="billing-charge-modal-error" role="alert">
            {error}
          </div>
        )}

        <div className="billing-charge-modal-actions">
          <button
            type="button"
            className="billing-charge-modal-primary"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading && <span className="billing-mini-spinner" aria-hidden="true" />}
            {loading ? "מבצע חיוב..." : "בצע חיוב"}
          </button>
          <button
            type="button"
            className="billing-charge-modal-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="billing-loading">
      <span className="billing-spinner" />
      <span>טוען נתוני חשבונות...</span>
    </div>
  );
}

function AccountFilters({ filters, setFilters }) {
  const update = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="billing-filters">
      <label className="billing-filter-label fixed-width">
        סטטוס חיוב
        <select
          className="billing-filter-control select"
          value={filters.j5Status}
          onChange={(event) => update("j5Status", event.target.value)}
        >
          <option value="all">הכל</option>
          <option value="pending_capture">בהמתנה לחיוב מנהל</option>
          <option value="j5_active">שריון פעיל</option>
          <option value="captured">העסקה חויבה</option>
          <option value="released">שוחרר</option>
          <option value="capture_pending">חיוב בתהליך</option>
          <option value="failed">נכשל</option>
          <option value="no_j5">ללא שריון</option>
        </select>
      </label>
      <label className="billing-filter-label fixed-width">
        סטטוס הצעה
        <select
          className="billing-filter-control select"
          value={filters.offerStatus}
          onChange={(event) => update("offerStatus", event.target.value)}
        >
          <option value="all">הכל</option>
          <option value="approved">אושרה</option>
          <option value="rejected">נדחתה</option>
          <option value="pending">ממתין לאישור</option>
        </select>
      </label>
      <label className="billing-filter-label fixed-width date-label">
מתאריך הגשת הצעה        <input
          className="billing-filter-control date-picker"
          type="date"
          value={filters.dateFrom}
          onChange={(event) => update("dateFrom", event.target.value)}
        />
      </label>
      <label className="billing-filter-label fixed-width date-label">
      עד תאריך הגשת הצעה
        <input
          className="billing-filter-control date-picker"
          type="date"
          value={filters.dateTo}
          onChange={(event) => update("dateTo", event.target.value)}
        />
      </label>
      <label className="billing-filter-label flex-grow">
        חיפוש חופשי
        <input
          className="billing-filter-control"
          placeholder="חפשו.."
          value={filters.search}
          onChange={(event) => update("search", event.target.value)}
        />
      </label>
    </div>
  );
}

function MobileDetailRow({ label, value, mono = false }) {
  return (
    <div className="billing-mobile-detail-row">
      <span className="billing-mobile-detail-label">{label}</span>
      <span className={mono ? "billing-mobile-detail-value mono" : "billing-mobile-detail-value"}>
        {value || "-"}
      </span>
    </div>
  );
}

function BillingMobileCard({
  item,
  config,
  role,
  userRole,
  expanded,
  onToggle,
  onChargeDeal,
  isCapturing,
}) {
  const status = getJ5Status(item, role);
  const canChargeDeal = role === "admin" && userRole === 1 && item.canChargeDeal === true;
  const showCapturedAmount = ["captured", "immediate_purchase_charged"].includes(status.statusKey);
  const showPendingAmount = status.statusKey === "capture_pending" || status.statusKey === "pending_capture";
  const showHoldAmount = isOpenJ5Status(status.statusKey) || isReleasedJ5Status(status.statusKey);

  return (
    <article className="billing-mobile-card">
      <div className="billing-mobile-card-top">
        <div className="billing-mobile-title-block">
          <h2 className="billing-mobile-title">{vehicleLabel(item) || "רכב ללא פרטים"}</h2>
        </div>
        <J5StatusBadge item={item} role={role} compact className="billing-mobile-status-badge" />
      </div>

      <div className="billing-mobile-main-grid">
        <div className="billing-mobile-main-item">
          <span>תאריך הצעה</span>
          <strong>{formatDate(offerSubmissionDate(item))}</strong>
        </div>
        <div className="billing-mobile-main-item">
          <span>סכום הצעה</span>
          <strong>{formatMoney(item.offered_price)}</strong>
        </div>
        <div className="billing-mobile-main-item">
          <span>סטטוס הצעה</span>
          <strong>{offerStatusLabel(item)}</strong>
        </div>
        <div className="billing-mobile-main-item">
          <span>סוג עסקה</span>
          <strong>{dealTypeLabel(item, status)}</strong>
        </div>
      </div>

      <button
        type="button"
        className="billing-mobile-toggle"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span>{expanded ? "סגירת פרטים" : "פרטים נוספים"}</span>
        <span className="billing-mobile-toggle-icon" aria-hidden="true">
          {expanded ? "−" : "+"}
        </span>
      </button>

      <div className={expanded ? "billing-mobile-details open" : "billing-mobile-details"}>
        <div className="billing-mobile-details-inner">
          {config.showSupplier && (
            <>
              <MobileDetailRow label="ספק" value={item.supplier_name} />
              <MobileDetailRow label="פרטי ספק" value={item.supplier_phone || item.supplier_email} />
            </>
          )}
          {config.showDealer && (
            <>
              <MobileDetailRow label="סוחר" value={item.dealer_name} />
              <MobileDetailRow label="פרטי סוחר" value={item.dealer_phone || item.dealer_email} />
            </>
          )}
          <MobileDetailRow label="סטטוס חיוב" value={status.description} />
          {showCapturedAmount && <MobileDetailRow label="סכום חיוב" value={formatMoney(chargeAmount(item) || item.offered_price)} />}
          {showPendingAmount && <MobileDetailRow label="סכום לחיוב" value={formatMoney(item.offered_price)} />}
          {showHoldAmount && <MobileDetailRow label="סכום שריון" value={formatMoney(item.reservation_hold_amount || 2000)} />}
          <MobileDetailRow label="תאריך שחרור" value={formatDate(item.released_at)} />
          <MobileDetailRow label="סיבת שחרור" value={releaseReasonLabel(item.release_reason)} />
          {role === "admin" && (
            <>
              <MobileDetailRow label="payme_sale_id" value={primaryPaymeSaleId(item)} mono />
              <MobileDetailRow label="סכום בפרטי חיוב" value={formatMoney(chargeAmount(item))} />
              <MobileDetailRow label="תאריך בפרטי חיוב" value={formatDate(chargeCreatedAt(item))} />
              <MobileDetailRow label="מזהה פרטי חיוב" value={chargePaymeSaleId(item)} mono />
            </>
          )}

          {config.actionColumn && (
            <div className="billing-mobile-action">
              {canChargeDeal || status.isActionableByAdmin ? (
                <div className="billing-action-cell">
                  <div className="billing-action-meta">
                    <span>שריון: {formatMoney(item.reservation_hold_amount || 2000)}</span>
                    <span>לחיוב: {formatMoney(item.offered_price)}</span>
                  </div>
                  <button
                    type="button"
                    disabled={!canChargeDeal || isCapturing}
                    title={canChargeDeal ? "" : item.chargeDealDisabledReason || DEAL_CHARGE_DISABLED_TOOLTIP}
                    onClick={canChargeDeal ? () => onChargeDeal(item) : undefined}
                    className="billing-charge-button"
                  >
                    {isCapturing && <span className="billing-mini-spinner" aria-hidden="true" />}
                    {isCapturing ? "מעבד..." : "חייב עסקה"}
                  </button>
                </div>
              ) : (
                <span className="text-xs text-slate-400">אין פעולה</span>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export default function BillingAccounts({ currentUser, role = "dealer" }) {
  const config = roleConfig[role] || roleConfig.dealer;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [captureMessage, setCaptureMessage] = useState("");
  const [capturingOfferId, setCapturingOfferId] = useState(null);
  const [pendingChargeItem, setPendingChargeItem] = useState(null);
  const [chargeModalError, setChargeModalError] = useState("");
  const [expandedMobileCards, setExpandedMobileCards] = useState({});
  const [filters, setFilters] = useState(initialFilters);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_LOAD);
  const userRole = Number(currentUser?.user_type);

  const loadItems = useCallback((signal) => {
    setLoading(true);
    setError("");

    return fetch(`${API_DOMAIN}${config.endpoint}`, {
      signal,
      credentials: "include",
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok || body?.success === false) {
          const loadError = new Error(body?.error || "load_failed");
          loadError.status = response.status;
          throw loadError;
        }
        return Array.isArray(body.items) ? body.items : [];
      })
      .then(setItems)
      .catch((err) => {
        if (err.name !== "AbortError") {
          if (err.status === 401 || err.status === 403) {
            setError("החיבור שלך פג או שאין לך הרשאה לצפות במסך זה. התחברי מחדש ונסי שוב.");
          } else {
            setError("לא ניתן לטעון את נתוני החשבונות. נסי שוב מאוחר יותר.");
          }
        }
      })
      .finally(() => setLoading(false));
  }, [config.endpoint]);

  useEffect(() => {
    const controller = new AbortController();

    loadItems(controller.signal);

    return () => controller.abort();
  }, [loadItems]);

  const handleChargeDeal = (item) => {
    if (role !== "admin" || userRole !== 1 || item?.canChargeDeal !== true) return;

    setChargeModalError("");
    setPendingChargeItem(item);
  };

  const confirmChargeDeal = async () => {
    const item = pendingChargeItem;
    if (role !== "admin" || userRole !== 1 || item?.canChargeDeal !== true) return;

    setCapturingOfferId(item.offer_id);
    setCaptureMessage("");
    setChargeModalError("");

    try {
      const response = await fetch(`${API_DOMAIN}/billing/chargeApprovedDeal`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offer_id: item.offer_id }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body?.success === false) {
        throw new Error(body?.error || body?.message || "חיוב העסקה נכשל");
      }

      setItems((prevItems) =>
        prevItems.map((currentItem) =>
          String(currentItem.offer_id) === String(item.offer_id)
            ? {
                ...currentItem,
                canChargeDeal: false,
                deal_charge_succeeded: true,
                deal_charge_pending: false,
                capture_pending: false,
                last_billing_transaction_status: "success",
                last_billing_transaction_type: "deal_charge",
                last_billing_transaction_failure_reason: "",
                deal_charge_amount: body?.data?.amount || item.offered_price,
                deal_charge_created_at: body?.data?.created_at || new Date().toISOString(),
                deal_charge_payme_sale_id:
                  body?.data?.payme_sale_id ||
                  body?.data?.sale_id ||
                  currentItem.deal_charge_payme_sale_id,
              }
            : currentItem
        )
      );
      setCaptureMessage(body?.message || "העסקה חויבה בפועל.");
      setPendingChargeItem(null);
      await loadItems();
    } catch (err) {
      const errorMessage = err.message || "לא ניתן לבצע חיוב עסקה כרגע.";
      setChargeModalError(errorMessage);
      setItems((prevItems) =>
        prevItems.map((currentItem) =>
          String(currentItem.offer_id) === String(item.offer_id)
            ? {
                ...currentItem,
                deal_charge_succeeded: false,
                deal_charge_pending: false,
                capture_pending: false,
                last_billing_transaction_status: "failed",
                last_billing_transaction_type: "deal_charge",
                last_billing_transaction_failure_reason: errorMessage,
                last_error: errorMessage,
              }
            : currentItem
        )
      );
    } finally {
      setCapturingOfferId(null);
    }
  };

  const visibleItems = useMemo(() => {
    if (!config.showFilters) return items;
    return filterAccountItems(items, filters, role);
  }, [config.showFilters, filters, items, role]);

  useEffect(() => {
    setVisibleCount(ITEMS_PER_LOAD);
    setExpandedMobileCards({});
  }, [visibleItems]);

  const displayedItems = useMemo(
    () => visibleItems.slice(0, visibleCount),
    [visibleCount, visibleItems]
  );
  const hasMoreItems = visibleCount < visibleItems.length;
  const loadMoreRef = useInfiniteScroll(
    () => setVisibleCount((count) => Math.min(count + ITEMS_PER_LOAD, visibleItems.length)),
    {
      enabled: !loading && !error && hasMoreItems,
      rootMargin: "0px 0px 240px 0px",
    }
  );

  const summary = useMemo(() => calcSummary(items, role), [items, role]);
  const summaryValues =
    role === "admin"
      ? [summary.total, summary.open, summary.released, summary.pendingAdmin, summary.noJ5]
      : role === "dealer"
        ? [summary.charged, summary.open, summary.released, summary.noJ5]
        : [summary.total, summary.open, summary.released, summary.noJ5];
  const toggleMobileCard = (key) => {
    setExpandedMobileCards((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (userRole && userRole !== config.allowedRole) {
    return (
      <main dir="rtl" className="min-h-screen bg-blue_gray-50 p-8 text-right text-slate-900 sm:p-4">
        <section className="mx-auto max-w-[900px] rounded-lg border border-slate-200 bg-white p-8">
          אין לך הרשאה לצפות במסך זה.
        </section>
      </main>
    );
  }

  return (
    <main dir="rtl" className="min-h-screen bg-blue_gray-50 px-8 py-7 pr-[8%] text-slate-950 md:px-4 md:pt-24">
      <DealChargeConfirmModal
        item={pendingChargeItem}
        loading={Boolean(capturingOfferId)}
        error={chargeModalError}
        onCancel={() => {
          if (!capturingOfferId) {
            setPendingChargeItem(null);
            setChargeModalError("");
          }
        }}
        onConfirm={confirmChargeDeal}
      />

      <section className="mx-auto flex flex-col gap-5">
        <header className="flex flex-wrap items-end justify-between gap-4 text-right">
          <div>
            <h1 className="text-3xl font-bold text-slate-950 sm:text-2xl">{config.title}</h1>
            <p className="mt-1 text-sm text-slate-500">{config.subtitle}</p>
          </div>
        </header>

        <div className="billing-summary-container">
          {config.summaryLabels.map((label, index) => (
            <SummaryCard key={label} label={label} value={summaryValues[index]} />
          ))}
        </div>

        {config.showFilters && <AccountFilters filters={filters} setFilters={setFilters} />}

        {captureMessage && (
          <div className="billing-action-message" role="status">
            {captureMessage}
          </div>
        )}

        <div className="billing-table-container billing-desktop-table">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <div className="flex min-h-[260px] items-center justify-center px-6 text-center text-rose-700">
              {error}
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="flex min-h-[260px] items-center justify-center px-6 text-center text-slate-500">
              לא נמצאו עסקאות להצגה
            </div>
          ) : (
            <div className="billing-table-wrapper">
              <table className="billing-table">
                <thead>
                  <tr>
                    <th className="px-4 py-3">תאריך הגשת הצעה</th>
                    {config.showSupplier && <th className="px-4 py-3">ספק</th>}
                    {config.showDealer && <th className="px-4 py-3">סוחר</th>}
                    <th className="px-4 py-3">רכב</th>
                    <th className="px-4 py-3">סכום הצעה</th>
                    <th className="px-4 py-3">סטטוס הצעה</th>
                    <th className="px-4 py-3">סטטוס חיוב</th>
                    <th className="px-4 py-3">תאריך שחרור</th>
                    <th className="px-4 py-3">סיבת שחרור</th>
                    {role === "admin" && <th className="px-4 py-3">payme_sale_id</th>}
                    {role === "admin" && <th className="px-4 py-3">פרטי חיוב</th>}
                    {config.actionColumn && <th className="px-4 py-3">פעולה</th>}
                  </tr>
                </thead>
                <tbody>
                  {displayedItems.map((item) => {
                    const status = getJ5Status(item, role);
                    const rowKey = rowKeyForItem(item);
                    const canChargeDeal = role === "admin" && userRole === 1 && item.canChargeDeal === true;
                    const isCapturing = capturingOfferId && String(capturingOfferId) === String(item.offer_id);

                    return (
                      <tr key={rowKey}>
                        <td className="px-4 py-3 text-slate-700">{formatDate(offerSubmissionDate(item))}</td>
                        {config.showSupplier && (
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">{item.supplier_name || "-"}</div>
                            <div className="text-xs text-slate-500">{item.supplier_phone || item.supplier_email || ""}</div>
                          </td>
                        )}
                        {config.showDealer && (
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">{item.dealer_name || "-"}</div>
                            <div className="text-xs text-slate-500">{item.dealer_phone || item.dealer_email || ""}</div>
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">{vehicleLabel(item) || "-"}</div>
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-900">{formatMoney(item.offered_price)}</td>
                        <td className="px-4 py-3 text-slate-700">{offerStatusLabel(item)}</td>
                        <td className="px-4 py-3">
                          <J5StatusBadge item={item} role={role} />
                          {!["captured", "immediate_purchase_charged"].includes(status.statusKey) && (
                            <div className="mt-1 max-w-[240px] text-xs leading-5 text-slate-500">
                              {status.description}
                            </div>
                          )}
                          {["captured", "immediate_purchase_charged"].includes(status.statusKey) && (
                            <div className="mt-2 text-xs font-semibold text-slate-700">
                              סכום חיוב: {formatMoney(chargeAmount(item) || item.offered_price)}
                            </div>
                          )}
                          {(status.statusKey === "capture_pending" || status.statusKey === "pending_capture") && (
                            <div className="mt-2 text-xs font-semibold text-slate-700">
                              סכום לחיוב: {formatMoney(item.offered_price)}
                            </div>
                          )}
                          {(isOpenJ5Status(status.statusKey) || isReleasedJ5Status(status.statusKey)) && (
                            <div className="mt-2 text-xs font-semibold text-slate-700">
                              סכום שריון: {formatMoney(item.reservation_hold_amount || 2000)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{formatDate(item.released_at)}</td>
                        <td className="max-w-[230px] px-4 py-3 text-slate-700">
                          {releaseReasonLabel(item.release_reason)}
                        </td>
                        {role === "admin" && (
                          <td
                            className="max-w-[180px] truncate px-4 py-3 font-mono text-xs text-slate-700"
                            title={primaryPaymeSaleId(item) || ""}
                          >
                            {primaryPaymeSaleId(item) || "-"}
                          </td>
                        )}
                        {role === "admin" && (
                          <td className="px-4 py-3 text-slate-700">
                            <div className="flex flex-col gap-1 text-xs">
                              <div><span className="font-semibold">סכום:</span> {formatMoney(chargeAmount(item))}</div>
                              <div><span className="font-semibold">תאריך:</span> {formatDate(chargeCreatedAt(item))}</div>
                              <div className="max-w-[180px] truncate font-mono" title={chargePaymeSaleId(item) || ""}>
                                <span className="font-semibold">ID:</span> {chargePaymeSaleId(item) || "-"}
                              </div>
                            </div>
                          </td>
                        )}
                        {config.actionColumn && (
                          <td className="px-4 py-3">
                            {canChargeDeal || status.isActionableByAdmin ? (
                              <div className="billing-action-cell">
                                <div className="billing-action-meta">
                                  <span>שריון: {formatMoney(item.reservation_hold_amount || 2000)}</span>
                                  <span>לחיוב: {formatMoney(item.offered_price)}</span>
                                </div>
                                <button
                                  type="button"
                                  disabled={!canChargeDeal || isCapturing}
                                  title={canChargeDeal ? "" : item.chargeDealDisabledReason || DEAL_CHARGE_DISABLED_TOOLTIP}
                                  onClick={canChargeDeal ? () => handleChargeDeal(item) : undefined}
                                  className="billing-charge-button"
                                >
                                  {isCapturing && <span className="billing-mini-spinner" aria-hidden="true" />}
                                  {isCapturing ? "מעבד..." : "חייב עסקה"}
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">אין פעולה</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="billing-mobile-list" aria-live="polite">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <div className="billing-mobile-state error">{error}</div>
          ) : visibleItems.length === 0 ? (
            <div className="billing-mobile-state">לא נמצאו עסקאות להצגה</div>
          ) : (
            displayedItems.map((item) => {
              const rowKey = rowKeyForItem(item);
              const isCapturing = capturingOfferId && String(capturingOfferId) === String(item.offer_id);

              return (
                <BillingMobileCard
                  key={rowKey}
                  item={item}
                  config={config}
                  role={role}
                  userRole={userRole}
                  expanded={Boolean(expandedMobileCards[rowKey])}
                  onToggle={() => toggleMobileCard(rowKey)}
                  onChargeDeal={handleChargeDeal}
                  isCapturing={Boolean(isCapturing)}
                />
              );
            })
          )}
        </div>

        {!loading && !error && visibleItems.length > 0 && (
          <div className="billing-lazy-footer">
            <div className="billing-lazy-count">
              מציג {displayedItems.length} מתוך {visibleItems.length}
            </div>
            {hasMoreItems && (
              <div ref={loadMoreRef} className="billing-lazy-sentinel" aria-hidden="true">
                <span className="billing-mini-spinner" />
              </div>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
